import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
    RiHome3Line,
    RiMenuLine,
    RiCloseLine,
    RiUserLine,
    RiShoppingCartLine,
    RiSearchLine,
    RiGridLine,
    RiSettings4Line,
    // RiHeartLine,
    RiShoppingBagLine
} from 'react-icons/ri'
// import { useTranslation } from 'react-i18next'

import { useEffect, useState } from 'react'

import routes from '@/routes/routes'
import { mainLogo, projectName } from "@/utils/constants/common"
import useAuthStore, { type UserDetails } from '@/stores/AuthStore'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import Input from '@/components/forms/Input/Input'
// import useCartStore from '@/stores/CartStore'

import './Navbar.css'

const Navbar = () => {
    const navigate = useNavigate()
    // const { t } = useTranslation('common')
    const location = useLocation()

    const { isLogged, getUserDetails } = useAuthStore()
    // const { items: cartItems } = useCartStore()
    
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(undefined)
    const [profileLoading, setProfileLoading] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    

    useEffect(() => {
        if (isLogged) {
            setProfileLoading(true)
            setTimeout(() => {
                const userData = getUserDetails()
                setUserDetails(userData)
                setProfileLoading(false)
            }, 500)
        }
    }, [isLogged, getUserDetails])
    
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20)
        }
        
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const isActive = (path: string) => location.pathname.startsWith(path)

    const navItems = [
        { path: routes.home, icon: RiHome3Line, label: 'Inicio' },
        { path: routes.products, icon: RiShoppingBagLine, label: 'Productos' },
        { path: routes.categories, icon: RiGridLine, label: 'Categorías' },
    ]

    const toggleMobileMenu = () => {
        setMobileMenuOpen(prev => !prev)
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`)
            setSearchOpen(false)
            setSearchQuery('')
        }
    }

    // const cartItemCount = cartItems?.reduce((total: number, item: any) => total + item.quantity, 0) || 0

    return (
        <nav className={`marketplace-navbar ${scrolled ? 'scrolled' : ''}`}>
            <div className="navbar-container">
                {/* Brand Section */}
                <div className="navbar-brand">
                    <Link to={routes.home} className="brand-link">
                        <img src={mainLogo.src} alt={mainLogo.alt} className="brand-logo" />
                        <span className="brand-text">{projectName}</span>
                    </Link>
                </div>

                {/* Search Section - Desktop */}
                <div className="navbar-search-desktop">
                    <form onSubmit={handleSearch} className="search-form">
                        <Input
                            name="search"
                            value={searchQuery}
                            setValue={setSearchQuery}
                            placeholder="Buscar productos..."
                            variant="bordered"
                            startContent={<RiSearchLine className="search-icon" />}
                            classNames="search-input"
                        />
                    </form>
                </div>

                {/* Navigation Links - Desktop */}
                <div className="navbar-nav-desktop">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                        >
                            <item.icon size={18} />
                            <span>{item.label}</span>
                        </Link>
                    ))}
                </div>

                {/* Actions Section */}
                <div className="navbar-actions">
                    {/* Search Toggle - Mobile */}
                    <button 
                        className="action-button search-toggle-mobile"
                        onClick={() => setSearchOpen(!searchOpen)}
                    >
                        <RiSearchLine size={20} />
                    </button>

                    {/* Wishlist */}
                    {/* {isLogged && (
                        <Link to="/wishlist" className="action-button">
                            <RiHeartLine size={20} />
                        </Link>
                    )} */}

                    {/* Cart */}
                    <Link to={routes.cart} className="action-button cart-button">
                        <RiShoppingCartLine size={20} />
                        {/* {cartItemCount > 0 && (
                            <span className="cart-badge">{cartItemCount}</span>
                        )} */}
                    </Link>

                    {/* User Menu */}
                    {isLogged ? (
                        <div className="user-menu">
                            {profileLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <Link to="/orders" className="action-button">
                                        <RiShoppingBagLine size={20} />
                                    </Link>
                                    
                                    {userDetails?.is_staff && (
                                        <Link to={routes.admin} className="action-button admin-link">
                                            <RiSettings4Line size={20} />
                                        </Link>
                                    )}
                                    
                                    <Link to={routes.profile} className="user-profile-link">
                                        <RiUserLine size={20} />
                                        <span className="user-name">
                                            {userDetails?.first_name || 'Perfil'}
                                        </span>
                                    </Link>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="auth-buttons">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(routes.login)}
                            >
                                Iniciar Sesión
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => navigate(routes.register)}
                            >
                                Registrarse
                            </Button>
                        </div>
                    )}

                    {/* Mobile Menu Toggle */}
                    <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                        {mobileMenuOpen ? <RiCloseLine size={24} /> : <RiMenuLine size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Search Bar */}
            {searchOpen && (
                <div className="mobile-search-bar">
                    <form onSubmit={handleSearch} className="mobile-search-form">
                        <Input
                            name="mobile-search"
                            value={searchQuery}
                            setValue={setSearchQuery}
                            placeholder="Buscar productos..."
                            variant="bordered"
                            startContent={<RiSearchLine className="search-icon" />}
                            classNames="search-input"
                        />
                    </form>
                </div>
            )}

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="mobile-menu">
                    <div className="mobile-menu-content">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <item.icon size={20} />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                        
                        {isLogged ? (
                            <>
                                <Link to={routes.profile} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                    <RiUserLine size={20} />
                                    <span>Perfil</span>
                                </Link>
                                <Link to="/orders" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                    <RiShoppingBagLine size={20} />
                                    <span>Mis Pedidos</span>
                                </Link>
                                {/* <Link to="/wishlist" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                    <RiHeartLine size={20} />
                                    <span>Lista de Deseos</span>
                                </Link> */}
                                {userDetails?.is_staff && (
                                    <Link to={routes.admin} className="mobile-nav-link admin-link" onClick={() => setMobileMenuOpen(false)}>
                                        <RiSettings4Line size={20} />
                                        <span>Panel de Admin</span>
                                    </Link>
                                )}
                            </>
                        ) : (
                            <div className="mobile-auth-section">
                                <Button variant="secondary" onClick={() => { navigate(routes.login); setMobileMenuOpen(false) }}>
                                    Iniciar Sesión
                                </Button>
                                <Button variant="primary" onClick={() => { navigate(routes.register); setMobileMenuOpen(false) }}>
                                    Registrarse
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    )
}

export default Navbar